import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, MapPin, User, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface InterviewSchedulerProps {
  candidateId: string;
  candidateName: string;
  interviewType: 'first' | 'second';
  onScheduled: () => void;
  onCancel: () => void;
}

export function InterviewScheduler({ 
  candidateId, 
  candidateName, 
  interviewType, 
  onScheduled, 
  onCancel 
}: InterviewSchedulerProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft(`interview_schedule_${interviewType}`, candidateId);
  
  const [formData, setFormData] = useState(() => {
    // Load from secure database draft
    if (draft) {
      return draft;
    }
    
    // Default values
    return {
      scheduledDate: '',
      scheduledTime: '',
      location: '',
      interviewer: '',
      notes: '',
      duration: '60' // minutes
    };
  });

  // Save to database whenever formData changes (debounced via hook)
  useEffect(() => {
    const hasData = Object.values(formData).some(v => v !== "" && v !== "60");
    if (hasData) {
      saveDraft(formData);
    }
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time
      const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      
      // Create interview record
      const { error } = await supabase
        .from('interviews')
        .insert({
          candidate_id: candidateId,
          interview_type: interviewType, // Use 'first' or 'second' directly
          scheduled_date: scheduledDateTime.toISOString(),
          status: 'scheduled',
          interview_data: {
            location: formData.location,
            interviewer: formData.interviewer,
            duration: parseInt(formData.duration),
            notes: formData.notes
          }
        });

      if (error) throw error;

      // Update workflow stage
      const nextStage = interviewType === 'first' ? 'first_interview' : 'second_interview';
      
      await supabase
        .from('hr_workflow_stages')
        .upsert({
          candidate_id: candidateId,
          stage_name: nextStage,
          status: 'in_progress',
          started_at: new Date().toISOString(),
          notes: `Συνέντευξη προγραμματισμένη για ${scheduledDateTime.toLocaleDateString('el-GR')} στις ${scheduledDateTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}`
        });

      // Update candidate status
      await supabase
        .from('candidates')
        .update({ 
          status: interviewType === 'first' ? 'first_interview_scheduled' : 'second_interview_scheduled'
        })
        .eq('id', candidateId);

      toast({
        title: "Επιτυχία",
        description: `Η ${interviewType === 'first' ? 'πρώτη' : 'δεύτερη'} συνέντευξη προγραμματίστηκε επιτυχώς`,
      });

      // Clear saved data after successful submission
      await deleteDraft();
      
      onScheduled();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Προγραμματισμός {interviewType === 'first' ? 'Πρώτης' : 'Δεύτερης'} Συνέντευξης
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Υποψήφιος: <span className="font-medium">{candidateName}</span>
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Ημερομηνία
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({...formData, scheduledDate: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ώρα
              </Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({...formData, scheduledTime: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Τοποθεσία
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="π.χ. Γραφείο HR, Αίθουσα συσκέψεων Α'"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="interviewer" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Συνεντευκτής
              </Label>
              <Input
                id="interviewer"
                value={formData.interviewer}
                onChange={(e) => setFormData({...formData, interviewer: e.target.value})}
                placeholder="Ονοματεπώνυμο συνεντευκτή"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">
                Διάρκεια (λεπτά)
              </Label>
              <Input
                id="duration"
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                min="15"
                max="180"
                step="15"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">
              Σημειώσεις (προαιρετικό)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Πρόσθετες πληροφορίες ή οδηγίες για τη συνέντευξη"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Αποθήκευση...' : 'Προγραμματισμός'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}